import {
  Alert,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useFacilityCampaigns } from "../hooks/useFacilityCampaigns";
import { CampaignTerms } from "./CampaignTerms";

export function CampaignPicker({
  facilityId,
  timezone,
  selectedId,
  offersDisabled,
  onSelect,
}: {
  facilityId: string;
  timezone: string;
  selectedId: string | null;
  offersDisabled: boolean;
  onSelect: (id: string | null) => void;
}) {
  const campaigns = useFacilityCampaigns(facilityId);
  if (campaigns.isLoading)
    return (
      <Typography role="status" variant="body2" color="text.secondary">
        Checking court offers…
      </Typography>
    );
  if (campaigns.isError)
    return (
      <Stack spacing={1}>
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              sx={{ whiteSpace: "nowrap" }}
              onClick={() => void campaigns.refetch()}
            >
              Retry offers
            </Button>
          }
        >
          Offers couldn&apos;t be loaded. You can continue without an offer.
        </Alert>
        {selectedId ? (
          <Button size="small" onClick={() => onSelect(null)}>
            Remove offer
          </Button>
        ) : null}
      </Stack>
    );
  if (!campaigns.data?.length && !selectedId) return null;
  return (
    <FormControl
      component="fieldset"
      sx={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      <FormLabel component="legend" id="court-offer-label">
        Court booking offer
      </FormLabel>
      <RadioGroup
        aria-labelledby="court-offer-label"
        value={offersDisabled ? "" : (selectedId ?? "")}
        onChange={(_event, value) => onSelect(value || null)}
      >
        <FormControlLabel value="" control={<Radio />} label="No offer" />
        {(campaigns.data ?? []).map((campaign) => (
          <FormControlLabel
            key={campaign.campaign_id}
            value={campaign.campaign_id}
            control={<Radio />}
            sx={{
              alignItems: "flex-start",
              my: 0.5,
              "& .MuiRadio-root": { pt: 0.25 },
            }}
            label={
              <>
                <Typography component="span" sx={{ fontWeight: 700 }}>
                  {campaign.name}
                </Typography>
                <CampaignTerms campaign={campaign} timezone={timezone} />
              </>
            }
          />
        ))}
      </RadioGroup>
      {selectedId &&
      !campaigns.data?.some(
        (campaign) => campaign.campaign_id === selectedId,
      ) ? (
        <FormHelperText error>
          This offer is no longer listed. Refresh the available offers.
        </FormHelperText>
      ) : (
        <FormHelperText>
          The best eligible offer is applied automatically. You can choose a
          different listed offer; eligibility is checked again before payment.
        </FormHelperText>
      )}
    </FormControl>
  );
}
